import { FC } from 'react';

import LinkIcon from '@mui/icons-material/Link';
import { Box, TypographyStyle } from '@mui/material';

import { navigate } from '@redwoodjs/router';

import HeadingLabeledValueCard from 'src/components/HeadingLabeledValueCard';
import { RiskCard } from 'src/pages/AddMerchantPage/steps/SummaryInformation/components/RisksSection/components/RiskCard';

interface InfoItem {
    title: string;
    value: string;
    subtitle?: string;
    titleSide?: 'top' | 'left';
    valueStyle?: TypographyStyle;
    textColor?: string;
    link?: string;
    navigateTo?: string;
    enableNavigation?: boolean;
    disableTrigger?: boolean;
    onClickFunction?: () => void;
}

interface InfoCardsProps {
    isHorizontal?: boolean;
    items: InfoItem[];
    height?: number | string;
}

const InformationCards: FC<InfoCardsProps> = ({ isHorizontal = false, items, height }) => {
    const createHandleClick = (item: InfoItem) => {
        return () => {
            if (item.enableNavigation && item.navigateTo) {
                navigate(item.navigateTo);
            } else if (item.onClickFunction && !item.disableTrigger) {
                item.onClickFunction();
            }
        };
    };

    const createKeyDownHandler = (isClickable: boolean, handleClick: () => void) => {
        if (!isClickable) return undefined;

        return (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleClick();
            }
        };
    };

    const getClickableStyles = (isClickable: boolean) => {
        if (!isClickable) return {};

        return {
            cursor: 'pointer',
            '&:focus': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
            },
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
            },
        };
    };

    const renderCardContent = (item: InfoItem, isClickable: boolean) => {
        if (item.titleSide === 'left') {
            return (
                <HeadingLabeledValueCard
                    title={item.title}
                    content={item.value}
                    valueStyle={{ whiteSpace: 'pre-line', ...item?.valueStyle }}
                />
            );
        }

        const cardValue = isClickable ? (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                    '&:hover': {
                        color: 'primary.main',
                    },
                }}
            >
                <LinkIcon sx={{ fontSize: 16, mr: 0.5, maxWidth: '20px' }} />
                <Box
                    sx={{
                        maxWidth: { lg: '180px', xl: '100%' },
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                    }}
                >
                    {item.value}
                </Box>
            </Box>
        ) : (
            item.value
        );

        return (
            <RiskCard
                label={item.title}
                value={cardValue}
                subtitle={item.subtitle}
                valueStyle={item.valueStyle}
            />
        );
    };

    return (
        <Box
            flex={`0 0 ${isHorizontal ? '100%' : '26%'}`}
            height={isHorizontal ? 'auto' : height}
            width="100%"
            display="flex"
            flexDirection={isHorizontal ? 'row' : 'column'}
            gap={2}
            sx={{
                '& > *': {
                    flexGrow: 1,
                    flexShrink: 1,
                    minHeight: 0,
                },
            }}
        >
            {items.map((item) => {
                const isClickable = item.enableNavigation || item.onClickFunction;
                const handleClick = createHandleClick(item);

                const commonProps = {
                    key: item.title,
                    display: 'flex',
                    flex: isHorizontal ? '1 1 0' : '1 0 auto',
                    width: isHorizontal ? '100%' : 'auto',
                };

                if (isClickable) {
                    return (
                        <Box
                            key={item.title}
                            component="button"
                            {...commonProps}
                            onClick={handleClick}
                            onKeyDown={createKeyDownHandler(isClickable, handleClick)}
                            aria-label={`${item.title}: ${item.value}`}
                            sx={{
                                ...getClickableStyles(isClickable),
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                textAlign: 'left',
                            }}
                        >
                            {renderCardContent(item, isClickable)}
                        </Box>
                    );
                }

                return <Box {...commonProps}>{renderCardContent(item, isClickable)}</Box>;
            })}
        </Box>
    );
};

export default InformationCards;
